import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "../api/client";
import { Plus, Trash2, Check, X, UserPlus, Users, BookOpen, MessageCircle, Apple, FolderOpen, FolderPlus, FileText, MessageSquare, HelpCircle, TrendingUp } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import NutritionProfileEditor from "../components/NutritionProfileEditor";
import FileUploader from "../components/FileUploader";

const TABS = [
  { id: "clients", label: "Clients", icon: Users },
  { id: "programs", label: "Folders", icon: BookOpen },
  { id: "nutrition", label: "Nutrition", icon: Apple },
  { id: "testimonials", label: "Testimonials", icon: MessageCircle },
];

export default function AdminDashboard() {
  const [tab, setTab] = useState("clients");
  // Shared so the Clients tab can deep-link into the Nutrition tab pre-selected to a client.
  const [nutritionClientId, setNutritionClientId] = useState("");
  // Total unread count across all folders + pending access requests, refreshed periodically.
  const [globalUnread, setGlobalUnread] = useState(0);
  const [pendingClientsCount, setPendingClientsCount] = useState(0);

  const refreshUnread = () => {
    api.get("/admin/programs").then((r) => {
      const total = (r.data || []).reduce((acc, p) => {
        const lastSeen = p.last_seen_admin_at ? new Date(p.last_seen_admin_at) : null;
        return acc + (p.updates || []).filter((u) =>
          u.author_role === "client" && (!lastSeen || new Date(u.created_at) > lastSeen)
        ).length;
      }, 0);
      setGlobalUnread(total);
    }).catch(() => {});
    api.get("/admin/clients").then((r) => {
      const pc = (r.data || []).filter((c) => c.approved === false).length;
      setPendingClientsCount(pc);
    }).catch(() => {});
  };
  useEffect(() => {
    refreshUnread();
    const t = setInterval(refreshUnread, 30000); // poll every 30s
    return () => clearInterval(t);
  }, [tab]);

  const goToNutrition = (clientId) => {
    setNutritionClientId(clientId);
    setTab("nutrition");
  };

  return (
    <div className="min-h-screen pt-24 pb-20 bg-[var(--bg)]" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <p className="overline mb-3">Studio</p>
        <h1 className="font-serif font-light text-4xl sm:text-5xl text-[color:var(--ink)] tracking-tight">
          Welcome, <em className="text-[color:var(--accent)] not-italic">Sopfie</em>.
        </h1>
        <p className="mt-3 text-[color:var(--ink-2)]">
          Tend to your clients, build their programs, and curate the words shared.
          {pendingClientsCount > 0 && (
            <span className="ml-2 inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-[color:var(--accent)]/15 text-[color:var(--accent)] text-xs font-medium" data-testid="studio-pending-banner">
              <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--accent)]" /> {pendingClientsCount} access request{pendingClientsCount !== 1 ? "s" : ""}
            </span>
          )}
          {globalUnread > 0 && (
            <span className="ml-2 inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-[color:var(--accent)]/15 text-[color:var(--accent)] text-xs font-medium" data-testid="studio-unread-banner">
              <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--accent)]" /> {globalUnread} new from your clients
            </span>
          )}
        </p>

        <div className="mt-10 flex gap-2 border-b border-[color:var(--line-soft)] overflow-x-auto">
          {TABS.map((t) => {
            const showDot = (globalUnread > 0 && (t.id === "clients" || t.id === "programs"))
              || (pendingClientsCount > 0 && t.id === "clients");
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`relative flex items-center gap-2 px-5 py-3 text-sm transition-colors -mb-px border-b-2 whitespace-nowrap ${
                  tab === t.id
                    ? "border-[color:var(--accent)] text-[color:var(--ink)]"
                    : "border-transparent text-[color:var(--ink-3)] hover:text-[color:var(--ink-2)]"
                }`}
                data-testid={`admin-tab-${t.id}`}
              >
                <t.icon size={15} strokeWidth={1.5} /> {t.label}
                {showDot && (
                  <span className="ml-1 inline-flex w-1.5 h-1.5 rounded-full bg-[color:var(--accent)]" data-testid={`admin-tab-${t.id}-dot`} />
                )}
              </button>
            );
          })}
        </div>

        <div className="mt-10">
          {tab === "clients" && <ClientsTab onOpenNutrition={goToNutrition} onOpenFolders={() => setTab("programs")} />}
          {tab === "programs" && <ProgramsTab />}
          {tab === "nutrition" && <NutritionTab activeClientId={nutritionClientId} setActiveClientId={setNutritionClientId} />}
          {tab === "testimonials" && <TestimonialsTab />}
        </div>
      </div>
    </div>
  );
}

/* ---------------- Clients ---------------- */
function ClientsTab({ onOpenNutrition, onOpenFolders }) {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [search, setSearch] = useState("");
  const [creatingFor, setCreatingFor] = useState(null); // client id we are creating a folder for
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");

  const refresh = async () => {
    const [c, p, np] = await Promise.all([
      api.get("/admin/clients"),
      api.get("/admin/programs"),
      api.get("/admin/nutrition/profiles").catch(() => ({ data: [] })),
    ]);
    setClients(c.data || []);
    setPrograms(p.data || []);
    setProfiles(np.data || []);
  };
  useEffect(() => { refresh(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setError(""); setOk("");
    try {
      await api.post("/admin/clients", form);
      setForm({ name: "", email: "", password: "" });
      setOk("Client created.");
      refresh();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  // One-click: create a folder for this client and jump straight inside it,
  // where Sopfie uploads the PDF and the Q/F/P timeline lives.
  const createFolderForClient = async (client) => {
    setCreatingFor(client.id);
    try {
      const firstName = (client.name || "").split(" ")[0] || "client";
      const { data } = await api.post("/admin/programs", {
        title: `${firstName}'s program`,
        description: null,
        client_id: client.id,
        workouts: [],
      });
      navigate(`/programs/${data.id}`);
    } catch (err) {
      alert(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setCreatingFor(null);
    }
  };

  const filtered = clients.filter((c) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (c.name || "").toLowerCase().includes(q) || (c.email || "").toLowerCase().includes(q);
  });

  const pending = clients.filter((c) => c.approved === false);
  const approved = filtered.filter((c) => c.approved !== false);
  const approvedExisting = approved.filter((c) => (c.plan_type || "existing") === "existing");
  const approvedOnline = approved.filter((c) => c.plan_type === "online");

  const approveClient = async (c) => {
    try {
      await api.post(`/admin/clients/${c.id}/approve`);
      refresh();
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail) || e.message);
    }
  };
  const rejectClient = async (c) => {
    if (!window.confirm(`Reject ${c.name}'s request? Their account and any data will be deleted.`)) return;
    try {
      await api.delete(`/admin/clients/${c.id}`);
      refresh();
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="grid lg:grid-cols-12 gap-8">
      <form onSubmit={create} className="lg:col-span-4 card-soft p-7 h-fit lg:sticky lg:top-28" data-testid="create-client-form">
        <h2 className="font-serif text-2xl text-[color:var(--ink)] flex items-center gap-2"><UserPlus size={18} strokeWidth={1.4} className="text-[color:var(--accent)]" /> New client</h2>
        <p className="text-sm text-[color:var(--ink-2)] mt-1">Create a profile so they can log in and see their program.</p>
        <div className="mt-5 space-y-3">
          <div><label className="overline block mb-1">Name</label><input className="input-warm" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-client-name" /></div>
          <div><label className="overline block mb-1">Email</label><input className="input-warm" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-client-email" /></div>
          <div><label className="overline block mb-1">Temp password</label><input className="input-warm" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="new-client-password" /></div>
        </div>
        {error && <p className="mt-3 text-sm text-[color:var(--accent-2)]" data-testid="new-client-error">{error}</p>}
        {ok && <p className="mt-3 text-sm text-[color:var(--ink)]">{ok}</p>}
        <button className="btn-primary mt-5 w-full justify-center" data-testid="new-client-submit"><Plus size={14} /> Create client</button>
      </form>

      <div className="lg:col-span-8">
        {/* PENDING REQUESTS */}
        {pending.length > 0 && (
          <div className="mb-8" data-testid="pending-clients-section">
            <h2 className="font-serif text-2xl text-[color:var(--ink)] flex items-center gap-2">
              <span className="inline-flex items-center justify-center min-w-[26px] h-[26px] px-1.5 rounded-full bg-[color:var(--accent)] text-[color:var(--bg)] text-xs font-medium">{pending.length}</span>
              Access requests
            </h2>
            <p className="text-xs text-[color:var(--ink-3)] mt-1 mb-4">
              People waiting for your approval to enter their space.
            </p>
            <div className="space-y-3">
              {pending.map((c) => (
                <article key={c.id} className="card-soft p-5 flex flex-wrap items-start justify-between gap-3" data-testid={`pending-client-${c.id}`}>
                  <div className="min-w-0">
                    <p className="font-serif text-xl text-[color:var(--ink)] truncate">{c.name}</p>
                    <a href={`mailto:${c.email}`} className="text-sm text-[color:var(--ink-2)] hover:text-[color:var(--accent)] break-all">{c.email}</a>
                    <p className="text-[10px] uppercase tracking-widest text-[color:var(--ink-3)] mt-2">
                      Requested {new Date(c.created_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button onClick={() => approveClient(c)} className="btn-primary !py-1.5 !px-4 !text-xs" data-testid={`pending-${c.id}-approve`}>
                      <Check size={12} strokeWidth={1.8} /> Approve
                    </button>
                    <button onClick={() => rejectClient(c)} className="btn-ghost !py-1.5 !px-4 !text-xs" data-testid={`pending-${c.id}-reject`}>
                      <X size={12} strokeWidth={1.8} /> Reject
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
          <div>
            <h2 className="font-serif text-2xl text-[color:var(--ink)]">Your clients <span className="text-[color:var(--ink-3)] text-sm">({clients.length - pending.length})</span></h2>
            <p className="text-xs text-[color:var(--ink-3)] mt-1">Each card opens that client's full universe — nutrition, folders, contact.</p>
          </div>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email…"
            className="input-warm !py-2 !text-sm max-w-xs"
            data-testid="clients-search"
          />
        </div>

        {/* Render a client's full card — reused in both groups. */}
        {(() => null)()}

        {approved.length === 0 && (
          <p className="text-[color:var(--ink-3)] italic" data-testid="clients-empty">
            {(clients.length - pending.length) === 0 ? "No active clients yet." : "No matches."}
          </p>
        )}

        {/* GROUP: Studio / existing clients (free) */}
        {approvedExisting.length > 0 && (
          <section className="space-y-4" data-testid="group-existing">
            <div className="flex items-center gap-3">
              <h3 className="font-serif text-xl text-[color:var(--ink)]">Studio clients</h3>
              <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[color:var(--bg-2)] text-[color:var(--ink-3)] border border-[color:var(--line-soft)]">free · in-person</span>
              <span className="text-xs text-[color:var(--ink-3)]">· {approvedExisting.length}</span>
            </div>
            {approvedExisting.map((c) => renderClientCard(c, { programs, profiles, onOpenNutrition, creatingFor, createFolderForClient }))}
          </section>
        )}

        {/* GROUP: Online clients (paid monthly) */}
        {approvedOnline.length > 0 && (
          <section className="space-y-4 mt-10" data-testid="group-online">
            <div className="flex items-center gap-3">
              <h3 className="font-serif text-xl text-[color:var(--ink)]">Online clients</h3>
              <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[color:var(--accent)]/10 text-[color:var(--accent)] border border-[color:var(--accent)]/20">paid · 650 kr/month</span>
              <span className="text-xs text-[color:var(--ink-3)]">· {approvedOnline.length}</span>
            </div>
            {approvedOnline.map((c) => renderClientCard(c, { programs, profiles, onOpenNutrition, creatingFor, createFolderForClient, showPaidUntil: true }))}
          </section>
        )}
      </div>
    </div>
  );
}

/* Render one full client card. Used by both "Studio" and "Online" groups. */
function renderClientCard(c, { programs, profiles, onOpenNutrition, creatingFor, createFolderForClient, showPaidUntil }) {
  const folders = programs.filter((p) => p.client_id === c.id);
  const profile = profiles.find((p) => p.client_id === c.id);
  const updates = folders.flatMap((f) => f.updates || []);
  const newestUpdate = updates.length ? updates.reduce((a, b) => (new Date(a.created_at) > new Date(b.created_at) ? a : b)) : null;
  const unreadFromClient = folders.reduce((acc, f) => {
    const lastSeen = f.last_seen_admin_at ? new Date(f.last_seen_admin_at) : null;
    return acc + (f.updates || []).filter((u) =>
      u.author_role === "client" && (!lastSeen || new Date(u.created_at) > lastSeen)
    ).length;
  }, 0);
  return (
    <article key={c.id} className="card-soft p-6 relative" data-testid={`client-${c.id}`}>
      {unreadFromClient > 0 && (
        <span
          className="absolute -top-2 -right-2 inline-flex items-center justify-center min-w-[24px] h-[24px] px-1.5 rounded-full bg-[color:var(--accent)] text-[color:var(--bg)] text-xs font-medium shadow-sm"
          data-testid={`client-${c.id}-unread`}
          title={`${unreadFromClient} new from ${c.name.split(" ")[0]}`}
        >
          {unreadFromClient}
        </span>
      )}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-serif text-2xl text-[color:var(--ink)] truncate">{c.name}</p>
          <a href={`mailto:${c.email}`} className="text-sm text-[color:var(--ink-2)] hover:text-[color:var(--accent)] break-all" data-testid={`client-${c.id}-email`}>{c.email}</a>
          <p className="text-[10px] uppercase tracking-widest text-[color:var(--ink-3)] mt-2">
            Member since {new Date(c.created_at).toLocaleDateString()}
            {showPaidUntil && c.paid_until && (
              <span className="ml-2 text-[color:var(--accent)]">· paid until {new Date(c.paid_until).toLocaleDateString()}</span>
            )}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => createFolderForClient(c)}
            disabled={creatingFor === c.id}
            className="btn-primary !py-1.5 !px-3 !text-xs"
            data-testid={`client-${c.id}-new-folder`}
          >
            <FolderPlus size={12} strokeWidth={1.6} /> {creatingFor === c.id ? "Creating…" : "New folder"}
          </button>
          <button
            onClick={() => onOpenNutrition && onOpenNutrition(c.id)}
            className="btn-ghost !py-1.5 !px-3 !text-xs"
            data-testid={`client-${c.id}-nutrition`}
          >
            <Apple size={12} strokeWidth={1.6} /> Nutrition
          </button>
        </div>
      </div>

      <div className="mt-5 grid sm:grid-cols-3 gap-3">
        {/* Nutrition summary */}
        <div className="rounded-2xl border border-[color:var(--line-soft)] p-4 bg-[var(--bg)]">
          <p className="overline flex items-center gap-1.5"><Apple size={11} strokeWidth={1.6} className="text-[color:var(--accent)]" /> Nutrition</p>
          {profile ? (
            <>
              <p className="text-sm text-[color:var(--ink)] mt-2 font-medium">
                {profile.target_kcal ? `${profile.target_kcal} kcal` : "—"}
              </p>
              <p className="text-xs text-[color:var(--ink-3)] mt-0.5 capitalize">
                Phase: {profile.phase || "—"}
              </p>
              {profile.weight_kg && (
                <p className="text-xs text-[color:var(--ink-3)]">{profile.weight_kg} kg · {profile.height_cm || "—"} cm</p>
              )}
            </>
          ) : (
            <p className="text-xs text-[color:var(--ink-3)] italic mt-2">No profile yet</p>
          )}
        </div>

        {/* Folders summary */}
        <div className="rounded-2xl border border-[color:var(--line-soft)] p-4 bg-[var(--bg)]">
          <p className="overline flex items-center gap-1.5"><FolderOpen size={11} strokeWidth={1.6} className="text-[color:var(--accent)]" /> Folders</p>
          <p className="text-sm text-[color:var(--ink)] mt-2 font-medium">{folders.length} folder{folders.length !== 1 ? "s" : ""}</p>
          <div className="text-xs text-[color:var(--ink-3)] mt-0.5 space-y-0.5">
            {folders.slice(0, 3).map((f) => (
              <Link key={f.id} to={`/programs/${f.id}`} className="block truncate hover:text-[color:var(--accent)]" data-testid={`client-${c.id}-folder-${f.id}`}>· {f.title}</Link>
            ))}
            {folders.length > 3 && <p className="italic">+ {folders.length - 3} more</p>}
            {folders.length === 0 && <p className="italic">No folders yet</p>}
          </div>
        </div>

        {/* Activity summary */}
        <div className="rounded-2xl border border-[color:var(--line-soft)] p-4 bg-[var(--bg)]">
          <p className="overline flex items-center gap-1.5"><MessageCircle size={11} strokeWidth={1.6} className="text-[color:var(--accent)]" /> Activity</p>
          <p className="text-sm text-[color:var(--ink)] mt-2 font-medium">{updates.length} note{updates.length !== 1 ? "s" : ""}</p>
          {newestUpdate ? (
            <p className="text-xs text-[color:var(--ink-3)] mt-0.5">
              Last: {new Date(newestUpdate.created_at).toLocaleDateString()} · {newestUpdate.author_role === "admin" ? "you" : c.name.split(" ")[0]}
            </p>
          ) : (
            <p className="text-xs text-[color:var(--ink-3)] italic mt-0.5">Quiet so far</p>
          )}
        </div>
      </div>
    </article>
  );
}

/* ---------------- Programs ---------------- */
function ProgramsTab() {
  const [clients, setClients] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [clientId, setClientId] = useState("");
  const [workouts, setWorkouts] = useState([
    { id: cryptoId(), title: "Workout A", day_label: "Day 1", description: "", exercises: [{ name: "", sets: 3, reps: "8-12", rest: "60s", notes: "" }] },
  ]);
  const [programFile, setProgramFile] = useState(null); // {id, original_filename}
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");

  function cryptoId() { return Math.random().toString(36).slice(2, 10); }

  const refresh = () => api.get("/admin/programs").then((r) => setPrograms(r.data || []));
  useEffect(() => {
    api.get("/admin/clients").then((r) => setClients(r.data || []));
    refresh();
  }, []);

  // Auto-fill the program title with the client's first name when a client is picked.
  // Sopfie can still override manually afterwards (we only auto-fill when the title
  // is empty or still equals a previously auto-generated title).
  const onClientChange = (newId) => {
    const prevClient = clients.find((c) => c.id === clientId);
    const nextClient = clients.find((c) => c.id === newId);
    const prevAuto = prevClient ? `${(prevClient.name || "").split(" ")[0]}'s program` : "";
    const nextAuto = nextClient ? `${(nextClient.name || "").split(" ")[0]}'s program` : "";
    setClientId(newId);
    if (!title.trim() || title === prevAuto) {
      setTitle(nextAuto);
    }
  };

  const addWorkout = () =>
    setWorkouts((ws) => [...ws, { id: cryptoId(), title: `Workout ${String.fromCharCode(65 + ws.length)}`, day_label: `Day ${ws.length + 1}`, description: "", exercises: [{ name: "", sets: 3, reps: "8-12", rest: "60s", notes: "" }] }]);
  const removeWorkout = (i) => setWorkouts((ws) => ws.filter((_, idx) => idx !== i));
  const updWorkout = (i, field, val) => setWorkouts((ws) => ws.map((w, idx) => (idx === i ? { ...w, [field]: val } : w)));
  const addExercise = (i) => setWorkouts((ws) => ws.map((w, idx) => idx === i ? { ...w, exercises: [...w.exercises, { name: "", sets: 3, reps: "8-12", rest: "60s", notes: "" }] } : w));
  const removeExercise = (i, j) => setWorkouts((ws) => ws.map((w, idx) => idx === i ? { ...w, exercises: w.exercises.filter((_, k) => k !== j) } : w));
  const updExercise = (i, j, field, val) => setWorkouts((ws) => ws.map((w, idx) => idx === i ? { ...w, exercises: w.exercises.map((e, k) => k === j ? { ...e, [field]: field === "sets" ? Number(val) || 0 : val } : e) } : w));

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setOk("");
    try {
      await api.post("/admin/programs", {
        title,
        description: description || null,
        client_id: clientId || null,
        workouts,
      });
      setTitle(""); setDescription(""); setClientId("");
      setWorkouts([{ id: cryptoId(), title: "Workout A", day_label: "Day 1", description: "", exercises: [{ name: "", sets: 3, reps: "8-12", rest: "60s", notes: "" }] }]);
      setOk("Folder created.");
      refresh();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this folder?")) return;
    await api.delete(`/admin/programs/${id}`);
    refresh();
  };

  return (
    <div className="grid lg:grid-cols-12 gap-8">
      <form onSubmit={submit} className="lg:col-span-7 card-soft p-7" data-testid="create-program-form">
        <h2 className="font-serif text-2xl text-[color:var(--ink)]">Create a folder</h2>
        <p className="text-sm text-[color:var(--ink-2)] mt-1">Pick a client — the folder name auto-fills. Upload the PDF now or later from inside the folder.</p>
        <div className="mt-5 grid sm:grid-cols-2 gap-3">
          <div>
            <label className="overline block mb-1">Title</label>
            <input required className="input-warm" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="program-title" />
          </div>
          <div>
            <label className="overline block mb-1">Assign to client</label>
            <Select value={clientId || "__none__"} onValueChange={(v) => onClientChange(v === "__none__" ? "" : v)}>
              <SelectTrigger className="input-warm !rounded-xl !border-[color:var(--line)] !bg-[var(--bg)] !text-[color:var(--ink)] !h-auto !py-3" data-testid="program-client">
                <SelectValue placeholder="— Template (no client) —" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__" data-testid="program-client-option-template">— Template (no client) —</SelectItem>
                {clients.map((c) => (
                  <SelectItem key={c.id} value={c.id} data-testid={`program-client-option-${c.id}`}>
                    {c.name} · {c.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-3">
          <label className="overline block mb-1">Description</label>
          <textarea rows={2} className="input-warm" value={description} onChange={(e) => setDescription(e.target.value)} data-testid="program-description" />
        </div>

        <div className="mt-5 rounded-2xl border border-[color:var(--line-soft)] bg-[var(--bg)] p-4">
          <p className="overline">Program file (PDF)</p>
          <p className="text-xs text-[color:var(--ink-3)] mt-1 mb-3">Upload the PDF you've built. Clients can download it and add notes & updates.</p>
          {programFile ? (
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-sm text-[color:var(--ink)]">📄 {programFile.original_filename}</span>
              <button type="button" onClick={() => setProgramFile(null)} className="text-xs text-[color:var(--accent-2)] underline" data-testid="program-file-remove">remove</button>
            </div>
          ) : (
            <FileUploader onUploaded={(f) => setProgramFile(f)} accept=".pdf,application/pdf" label="Upload PDF" testid="program-file-upload" />
          )}
        </div>

        <details className="mt-5 group">
          <summary className="cursor-pointer text-sm text-[color:var(--ink-2)] hover:text-[color:var(--ink)] select-none" data-testid="toggle-inline-builder">
            <span className="group-open:hidden">+ Or build the program inline (workouts & exercises)</span>
            <span className="hidden group-open:inline">− Hide inline builder</span>
          </summary>
          <div className="mt-4 space-y-5">
          {workouts.map((w, i) => (
            <div key={w.id} className="border border-[color:var(--line-soft)] rounded-2xl p-5 bg-[var(--bg)]" data-testid={`workout-builder-${i}`}>
              <div className="flex items-center justify-between gap-2">
                <input className="input-warm !w-auto flex-1 mr-2" value={w.title} onChange={(e) => updWorkout(i, "title", e.target.value)} data-testid={`workout-title-${i}`} placeholder="Workout title" />
                <input className="input-warm !w-auto w-32" value={w.day_label} onChange={(e) => updWorkout(i, "day_label", e.target.value)} data-testid={`workout-day-${i}`} placeholder="Day" />
                {workouts.length > 1 && (
                  <button type="button" onClick={() => removeWorkout(i)} className="text-[color:var(--ink-3)] hover:text-[color:var(--accent-2)]" data-testid={`workout-remove-${i}`}><Trash2 size={16} /></button>
                )}
              </div>
              <textarea rows={1} className="input-warm mt-2" value={w.description} onChange={(e) => updWorkout(i, "description", e.target.value)} placeholder="Workout note (optional)" />

              <div className="mt-4 space-y-3">
                {w.exercises.map((ex, j) => (
                  <div key={j} className="grid grid-cols-12 gap-2 items-start" data-testid={`exercise-builder-${i}-${j}`}>
                    <input className="input-warm col-span-12 sm:col-span-4" placeholder="Exercise name" value={ex.name} onChange={(e) => updExercise(i, j, "name", e.target.value)} />
                    <input className="input-warm col-span-3 sm:col-span-1" type="number" min={0} placeholder="Sets" value={ex.sets} onChange={(e) => updExercise(i, j, "sets", e.target.value)} />
                    <input className="input-warm col-span-3 sm:col-span-2" placeholder="Reps" value={ex.reps} onChange={(e) => updExercise(i, j, "reps", e.target.value)} />
                    <input className="input-warm col-span-3 sm:col-span-2" placeholder="Rest" value={ex.rest} onChange={(e) => updExercise(i, j, "rest", e.target.value)} />
                    <input className="input-warm col-span-3 sm:col-span-2" placeholder="Notes" value={ex.notes} onChange={(e) => updExercise(i, j, "notes", e.target.value)} />
                    <button type="button" onClick={() => removeExercise(i, j)} className="col-span-12 sm:col-span-1 text-[color:var(--ink-3)] hover:text-[color:var(--accent-2)] flex items-center justify-center"><X size={16} /></button>
                  </div>
                ))}
                <button type="button" onClick={() => addExercise(i)} className="btn-ghost !py-1.5 !px-3 !text-xs" data-testid={`add-exercise-${i}`}><Plus size={12} /> Add exercise</button>
              </div>
            </div>
          ))}
          <button type="button" onClick={addWorkout} className="btn-ghost" data-testid="add-workout"><Plus size={14} /> Add workout</button>
          </div>
        </details>

        {error && <p className="mt-4 text-sm text-[color:var(--accent-2)]" data-testid="program-error">{error}</p>}
        {ok && <p className="mt-4 text-sm text-[color:var(--ink)]">{ok}</p>}
        <button className="btn-primary mt-6 w-full justify-center" data-testid="program-submit">Save folder</button>
      </form>

      <div className="lg:col-span-5">
        <h2 className="font-serif text-2xl text-[color:var(--ink)] mb-2">Folders <span className="text-[color:var(--ink-3)] text-sm">({programs.length})</span></h2>
        <p className="text-xs text-[color:var(--ink-3)] mb-4">Open a folder to upload the program, read questions, and reply to your client.</p>
        <div className="space-y-3" data-testid="programs-list">
          {programs.map((p) => {
            const client = clients.find((c) => c.id === p.client_id);
            const updates = p.updates || [];
            const qCount = updates.filter((u) => u.category === "question").length;
            const fCount = updates.filter((u) => u.category === "feedback").length;
            const pCount = updates.filter((u) => u.category === "progression").length;
            const lastSeen = p.last_seen_admin_at ? new Date(p.last_seen_admin_at) : null;
            const unread = updates.filter((u) =>
              u.author_role === "client" && (!lastSeen || new Date(u.created_at) > lastSeen)
            ).length;
            return (
              <div key={p.id} className="card-soft p-5 group relative" data-testid={`admin-program-${p.id}`}>
                {unread > 0 && (
                  <span
                    className="absolute -top-2 -right-2 inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full bg-[color:var(--accent)] text-[color:var(--bg)] text-[11px] font-medium shadow-sm"
                    data-testid={`admin-program-${p.id}-unread`}
                    title={`${unread} new from your client`}
                  >
                    {unread}
                  </span>
                )}
                <div className="flex items-start gap-3">
                  <FolderOpen size={22} strokeWidth={1.4} className="text-[color:var(--accent)] mt-1 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-serif text-xl text-[color:var(--ink)] truncate">{p.title}</p>
                    <p className="text-xs text-[color:var(--ink-3)] mt-1">
                      {client ? `for ${client.name}` : "Template"}
                    </p>
                    <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-[color:var(--ink-2)]">
                      {(p.files && p.files.length > 0) ? (
                        <span className="inline-flex items-center gap-1" data-testid={`admin-program-${p.id}-pdf`}>
                          <FileText size={12} strokeWidth={1.5} /> {p.files.length} file{p.files.length !== 1 ? "s" : ""}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 italic text-[color:var(--ink-3)]" data-testid={`admin-program-${p.id}-pdf`}>
                          <FileText size={12} strokeWidth={1.5} /> no files
                        </span>
                      )}
                      <span className="inline-flex items-center gap-1" title="Questions"><HelpCircle size={12} strokeWidth={1.5} /> {qCount}</span>
                      <span className="inline-flex items-center gap-1" title="Feedback"><MessageSquare size={12} strokeWidth={1.5} /> {fCount}</span>
                      <span className="inline-flex items-center gap-1" title="Progressions"><TrendingUp size={12} strokeWidth={1.5} /> {pCount}</span>
                    </div>
                  </div>
                  <button onClick={() => remove(p.id)} className="text-[color:var(--ink-3)] hover:text-[color:var(--accent-2)]" data-testid={`delete-program-${p.id}`} title="Delete folder"><Trash2 size={15} /></button>
                </div>
                <div className="mt-4 pt-3 border-t border-[color:var(--line-soft)] flex justify-end">
                  <Link to={`/programs/${p.id}`} className="btn-primary !py-1.5 !px-4 !text-xs" data-testid={`admin-program-${p.id}-open`}>
                    Open folder →
                  </Link>
                </div>
              </div>
            );
          })}
          {programs.length === 0 && <p className="text-[color:var(--ink-3)] italic">No folders yet.</p>}
        </div>
      </div>
    </div>
  );
}

/* ---------------- Testimonials ---------------- */
function TestimonialsTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", content: "", role_label: "", rating: 5 });
  const [error, setError] = useState("");

  const refresh = () => api.get("/admin/testimonials").then((r) => setItems(r.data || []));
  useEffect(() => { refresh(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.post("/admin/testimonials", form);
      setForm({ name: "", content: "", role_label: "", rating: 5 });
      refresh();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const approve = async (id) => { await api.put(`/admin/testimonials/${id}/approve`); refresh(); };
  const remove = async (id) => { if (window.confirm("Delete?")) { await api.delete(`/admin/testimonials/${id}`); refresh(); } };

  const pending = items.filter((i) => !i.approved);
  const approved = items.filter((i) => i.approved);

  return (
    <div className="grid lg:grid-cols-12 gap-8">
      <form onSubmit={create} className="lg:col-span-5 card-soft p-7 h-fit" data-testid="create-testimonial-form">
        <h2 className="font-serif text-2xl text-[color:var(--ink)]">Add a testimonial</h2>
        <p className="text-sm text-[color:var(--ink-2)] mt-1">Will be published immediately.</p>
        <div className="mt-5 space-y-3">
          <input required className="input-warm" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-testimonial-name" />
          <textarea required minLength={4} rows={4} className="input-warm" placeholder="Their words…" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} data-testid="new-testimonial-content" />
          <input className="input-warm" placeholder="Label (optional, e.g. Personal Training Client)" value={form.role_label} onChange={(e) => setForm({ ...form, role_label: e.target.value })} data-testid="new-testimonial-label" />
        </div>
        {error && <p className="mt-3 text-sm text-[color:var(--accent-2)]">{error}</p>}
        <button className="btn-primary mt-5 w-full justify-center" data-testid="new-testimonial-submit"><Plus size={14} /> Publish</button>
      </form>

      <div className="lg:col-span-7 space-y-8">
        <section>
          <h2 className="font-serif text-2xl text-[color:var(--ink)] mb-3">Awaiting your blessing <span className="text-[color:var(--ink-3)] text-sm">({pending.length})</span></h2>
          <div className="space-y-3" data-testid="pending-testimonials">
            {pending.length === 0 && <p className="text-[color:var(--ink-3)] italic text-sm">Nothing pending.</p>}
            {pending.map((t) => (
              <div key={t.id} className="card-soft p-5" data-testid={`pending-${t.id}`}>
                <p className="font-serif text-lg text-[color:var(--ink)]">"{t.content}"</p>
                <div className="flex justify-between items-center mt-3">
                  <p className="text-xs text-[color:var(--ink-3)]">{t.name} {t.role_label && `· ${t.role_label}`}</p>
                  <div className="flex gap-2">
                    <button onClick={() => approve(t.id)} className="btn-ghost !py-1.5 !px-3 !text-xs" data-testid={`approve-${t.id}`}><Check size={12} /> Approve</button>
                    <button onClick={() => remove(t.id)} className="text-[color:var(--ink-3)] hover:text-[color:var(--accent-2)]" data-testid={`reject-${t.id}`}><Trash2 size={14} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
        <section>
          <h2 className="font-serif text-2xl text-[color:var(--ink)] mb-3">Published <span className="text-[color:var(--ink-3)] text-sm">({approved.length})</span></h2>
          <div className="space-y-3" data-testid="approved-testimonials">
            {approved.map((t) => (
              <div key={t.id} className="card-soft p-5" data-testid={`approved-${t.id}`}>
                <p className="font-serif text-lg text-[color:var(--ink)]">"{t.content}"</p>
                <div className="flex justify-between items-center mt-3">
                  <p className="text-xs text-[color:var(--ink-3)]">{t.name} {t.role_label && `· ${t.role_label}`}</p>
                  <button onClick={() => remove(t.id)} className="text-[color:var(--ink-3)] hover:text-[color:var(--accent-2)]" data-testid={`delete-${t.id}`}><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
            {approved.length === 0 && <p className="text-[color:var(--ink-3)] italic text-sm">None published yet.</p>}
          </div>
        </section>
      </div>
    </div>
  );
}

/* ---------------- Nutrition (per-client profile) ---------------- */
function NutritionTab({ activeClientId, setActiveClientId }) {
  const [clients, setClients] = useState([]);

  useEffect(() => {
    api.get("/admin/clients").then((r) => {
      setClients(r.data || []);
      if (!activeClientId && r.data?.length) setActiveClientId(r.data[0].id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const active = clients.find((c) => c.id === activeClientId);

  return (
    <div className="grid lg:grid-cols-12 gap-6">
      <aside className="lg:col-span-3">
        <h2 className="font-serif text-xl text-[color:var(--ink)] mb-3 flex items-center gap-2">
          <Apple size={16} strokeWidth={1.4} className="text-[color:var(--accent)]" /> Pick a client
        </h2>
        <div className="space-y-1.5 max-h-[70vh] overflow-auto pr-1" data-testid="nutrition-client-list">
          {clients.length === 0 && <p className="text-sm text-[color:var(--ink-3)] italic">No clients yet.</p>}
          {clients.map((c) => (
            <button
              key={c.id}
              onClick={() => setActiveClientId(c.id)}
              className={`w-full text-left px-3 py-2 rounded-xl border transition-colors ${
                activeClientId === c.id
                  ? "bg-[var(--bg-2)] border-[color:var(--accent)]/40"
                  : "border-transparent hover:bg-[var(--bg-2)]/60"
              }`}
              data-testid={`nutrition-client-${c.id}`}
            >
              <p className="text-sm text-[color:var(--ink)] truncate">{c.name}</p>
              <p className="text-xs text-[color:var(--ink-3)] truncate">{c.email}</p>
            </button>
          ))}
        </div>
      </aside>

      <div className="lg:col-span-9">
        {active ? (
          <NutritionProfileEditor mode="admin" clientId={active.id} clientName={active.name} key={active.id} />
        ) : (
          <p className="text-[color:var(--ink-3)] italic">Pick a client to view or edit their nutrition profile.</p>
        )}
      </div>
    </div>
  );
}


